/**
 * Dietitian Registration Form Component (Step 1)
 *
 * Handles the initial registration step with email, password, and name fields.
 * Includes real-time validation, error handling, and accessibility features.
 * Integrates with existing UI components and theme system.
 *
 * @module components/dietician-registration/DieticianRegistrationForm
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { useTheme } from '@/hooks/useTheme';
import { useDieticianRegistrationFormState } from '@/hooks/useDieticianRegistrationFormState';
import type { DieticianRegistrationFormData } from '@/hooks/useDieticianRegistrationFormState';
import { cn } from '@/utils/cn';

export interface DieticianRegistrationFormProps {
  onSubmit: (data: DieticianRegistrationFormData) => Promise<void>;
  initialData?: Partial<DieticianRegistrationFormData>;
  isLoading: boolean;
  errors: Record<string, string>;
}

export function DieticianRegistrationForm(props: DieticianRegistrationFormProps) {
  // Hooks
  const { isDark } = useTheme();
  const {
    formData,
    fieldErrors,
    fieldRefs,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit
  } = useDieticianRegistrationFormState({
    initialData: props.initialData,
    onSubmit: props.onSubmit
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form error display */}
      {props.errors.form && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">{props.errors.form}</p>
        </div>
      )}

      {/* Name fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="First Name"
          required
          error={fieldErrors.firstName || props.errors.firstName}
        >
          <Input
            ref={(el) => { fieldRefs.current.firstName = el; }}
            type="text"
            value={formData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleFieldBlur('firstName')}
            placeholder="Enter your first name"
            disabled={props.isLoading}
            autoComplete="given-name"
          />
        </FormField>

        <FormField
          label="Last Name"
          required
          error={fieldErrors.lastName || props.errors.lastName}
        >
          <Input
            ref={(el) => { fieldRefs.current.lastName = el; }}
            type="text"
            value={formData.lastName}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            onBlur={() => handleFieldBlur('lastName')}
            placeholder="Enter your last name"
            disabled={props.isLoading}
            autoComplete="family-name"
          />
        </FormField>
      </div>

      {/* Email field */}
      <FormField
        label="Email Address"
        required
        error={fieldErrors.email || props.errors.email}
          helperText="We'll use this email for important account notifications"
      >
        <Input
          ref={(el) => { fieldRefs.current.email = el; }}
          type="email"
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          onBlur={() => handleFieldBlur('email')}
          placeholder="Enter your email address"
          disabled={props.isLoading}
          autoComplete="email"
        />
      </FormField>

      {/* Password Fields Row */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Password Field */}
          <FormField
            label="Password"
            required
            error={fieldErrors.password || props.errors.password}
          >
            <Input
              ref={(el) => { fieldRefs.current.password = el; }}
              type="password"
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              onBlur={() => handleFieldBlur('password')}
              placeholder="Create a strong password"
              disabled={props.isLoading}
              autoComplete="new-password"
            />
          </FormField>

          {/* Confirm Password Field */}
          <FormField
            label="Confirm Password"
            required
            error={fieldErrors.confirmPassword || props.errors.confirmPassword}
          >
            <Input
              ref={(el) => { fieldRefs.current.confirmPassword = el; }}
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
              onBlur={() => handleFieldBlur('confirmPassword')}
              placeholder="Confirm your password"
              disabled={props.isLoading}
              autoComplete="new-password"
            />
          </FormField>
        </div>
        {/* Password Helper Text */}
        <p className={cn(
          "text-sm",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Must be at least 8 characters with uppercase, lowercase, and numbers
        </p>
      </div>

      {/* Submit button */}
      <Button
        type="submit"
        className="w-full"
        disabled={props.isLoading}
        loading={props.isLoading}
      >
        Create Account
      </Button>

      {/* Terms and Privacy */}
      <p className={cn(
        "text-xs text-center",
        isDark ? "text-secondary-400" : "text-secondary-600"
      )}>
        By creating an account, you agree to our{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Terms of Service (opens in new window)"
          className={cn(
            "hover:underline inline-flex items-center gap-1",
            isDark ? "text-primary-400" : "text-primary-600"
          )}
        >
          Terms of Service
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>{' '}
        and{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Privacy Policy (opens in new window)"
          className={cn(
            "hover:underline inline-flex items-center gap-1",
            isDark ? "text-primary-400" : "text-primary-600"
          )}
        >
          Privacy Policy
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </p>
    </form>
  );
}